import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Certificate, Target,
  GraduationCap, Buildings, Quotes, ShieldStar, Handshake, FirstAid,
} from "@phosphor-icons/react";
import Seo from "@/components/Seo";

// ---------- S. Williams (Founder) ----------
const WILLIAMS_CREDENTIALS = [
  { icon: ShieldCheck,   label: "MD State Police HQL Certified Instructor" },
  { icon: ShieldCheck,   label: "MD Special Police Licensed · Active" },
  { icon: ShieldCheck,   label: "D.C. Firearms Instructor Certified" },
  { icon: ShieldCheck,   label: "Utah Firearms Instructor Certified" },
  { icon: Certificate,   label: "NRA Certified Instructor" },
  { icon: Target,        label: "NRA Range Safety Officer" },
  { icon: Target,        label: "Sabre Red OC Instructor" },
  { icon: Target,        label: "ASP Baton · Handcuffs · Flashlight Instructor" },
  { icon: GraduationCap, label: "U.S. LawShield® Training Partner" },
  { icon: Certificate,   label: "American Red Cross First Aid / CPR / AED" },
  { icon: Buildings,     label: "Insured & Bonded · MD Business Registered" },
];

const WILLIAMS_CAREER = [
  {
    year: "2020–2024",
    title: "Law Enforcement · Washington, D.C.",
    detail: "Four years serving as a sworn law-enforcement officer in Washington, D.C. — armed assignments across healthcare, corporate, and residential posts in one of the busiest urban jurisdictions in the country. Direct experience in patrol, use-of-force decision-making, de-escalation, and legal application of firearms and less-lethal tools.",
  },
  {
    year: "Present",
    title: "Active Law Enforcement · Maryland",
    detail: "Currently sworn and commissioned as an armed law-enforcement officer in the State of Maryland — actively serving under state law-enforcement authority while running the training academy.",
  },
  {
    year: "2024",
    title: "Founded Secrecy Executive Training Academy",
    detail: "Launched the academy in Calverton, Maryland to bring the same discipline and legal rigor of law-enforcement–level training to civilians, security professionals, and protective agents.",
  },
];

// ---------- Derek Kinard (Instructor) ----------
const KINARD_CREDENTIALS = [
  { icon: ShieldStar,    label: "Active Maryland Police Officer" },
  { icon: GraduationCap, label: "2023 Anne Arundel County Police Academy Graduate" },
  { icon: Handshake,     label: "Science-Based Interviewing" },
  { icon: Target,        label: "Passenger & Commercial Vehicle Interdiction" },
  { icon: ShieldCheck,   label: "Combat 102" },
  { icon: FirstAid,      label: "First Aid" },
  { icon: FirstAid,      label: "Tactical First Aid" },
];

const KINARD_CAREER = [
  {
    year: "2023",
    title: "Anne Arundel County Police Academy",
    detail: "Graduated the Anne Arundel County Police Academy — one of the most rigorous municipal training programs in the Baltimore–Washington corridor. Trained to state standards across law, use-of-force, criminal procedure, defensive tactics, and firearms qualification.",
  },
  {
    year: "Present",
    title: "Active Maryland Police Officer",
    detail: "Currently sworn and actively serving as a Maryland police officer — daily field experience in patrol, situational awareness, communication under pressure, and lawful application of force. Real cases inform every lesson he teaches.",
  },
  {
    year: "Present",
    title: "Instructor · Secrecy Executive Training Academy",
    detail: "Focuses on connecting classroom instruction with practical, real-world application — decision-making, officer and civilian safety, communication, situational awareness, and professional conduct. Students learn from an instructor who actively works in the profession.",
  },
];

function InstructorHero({ id, fileNo, role, photoSrc, photoAlt, name, intro, stats }) {
  return (
    <section id={id} className="border-b border-white/10 pt-24 pb-16 md:pb-24" data-testid={`${id}-hero`}>
      <div className="container-x grid grid-cols-12 gap-8 lg:gap-14 items-center">
        <div className="col-span-12 md:col-span-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-[#FFD300]/10 blur-3xl rounded-full pointer-events-none" />
            <div className="relative border border-white/15 bg-[#0A0A0A] overflow-hidden">
              <img
                src={photoSrc}
                alt={photoAlt}
                className="w-full h-auto block"
                data-testid={`${id}-photo`}
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black via-black/70 to-transparent">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#FFD300]">{role}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-12 md:col-span-7">
          <div className="overline mb-4 flex items-center gap-3">
            <span className="w-10 h-px bg-[#FFD300]" />
            FILE {fileNo} — INSTRUCTOR PROFILE
          </div>
          <h1
            className="font-heading text-5xl md:text-7xl tracking-tighter leading-[0.9] mb-6"
            data-testid={`${id}-name`}
          >
            {name}
          </h1>
          <p className="text-neutral-300 text-lg leading-relaxed mb-6 max-w-2xl">
            {intro}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 max-w-2xl">
            {stats.map((s) => (
              <div key={s.l} className="bg-black p-5" data-testid={`${id}-stat-${s.l.replace(/\s/g,'-').toLowerCase()}`}>
                <div className="font-heading text-3xl md:text-4xl tracking-tighter text-[#FFD300]">{s.n}</div>
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-400 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CareerTimeline({ id, sectionNo, heading, headingHighlight, entries }) {
  return (
    <section className="section-pad border-b border-white/10 bg-[#050505]" data-testid={`${id}-career`}>
      <div className="container-x">
        <div className="overline mb-4">// {sectionNo} — BACKGROUND</div>
        <h2 className="font-heading text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-14 max-w-3xl">
          {heading}<br/><span className="text-[#FFD300]">{headingHighlight}</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {entries.map((c, i) => (
            <div key={c.title} className="bg-[#050505] p-8 md:p-10 relative" data-testid={`${id}-career-${i}`}>
              <div className="font-mono text-[11px] tracking-[0.22em] text-[#FFD300] uppercase mb-4">// {c.year}</div>
              <h3 className="font-heading text-2xl md:text-3xl tracking-tight leading-tight mb-4">{c.title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed">{c.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CredentialsGrid({ id, sectionNo, headingA, headingB, subline, credentials }) {
  return (
    <section className="section-pad" data-testid={`${id}-credentials`}>
      <div className="container-x">
        <div className="grid grid-cols-12 gap-8 mb-14">
          <div className="col-span-12 md:col-span-7">
            <div className="overline mb-4">// {sectionNo} — CREDENTIALS</div>
            <h2 className="font-heading text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              {headingA}<br/>
              <span className="text-[#FFD300]">{headingB}</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:self-end">
            <p className="text-neutral-400 leading-relaxed text-lg">{subline}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10" data-testid={`${id}-credentials-grid`}>
          {credentials.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="bg-black p-6 flex items-start gap-4 group hover:bg-[#0E0E0E] transition-colors" data-testid={`${id}-credential-${i}`}>
                <div className="w-10 h-10 flex items-center justify-center bg-[#FFD300]/10 flex-shrink-0 group-hover:bg-[#FFD300]/20 transition-colors">
                  <Icon size={20} weight="duotone" className="text-[#FFD300]" />
                </div>
                <div className="font-mono text-sm text-neutral-200 leading-snug pt-2">
                  {c.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function InstructorQuote({ id, children, attribution, ctaHeading, ctaBody }) {
  return (
    <section className="border-y border-white/10 bg-[#050505] section-pad" data-testid={`${id}-quote`}>
      <div className="container-x grid grid-cols-12 gap-12 items-center">
        <div className="col-span-12 md:col-span-8">
          <Quotes size={48} weight="duotone" className="text-[#FFD300] mb-6" />
          <blockquote className="font-heading text-3xl md:text-5xl tracking-tighter leading-[1.05] mb-8">
            {children}
          </blockquote>
          <div className="font-mono text-xs tracking-[0.2em] uppercase text-neutral-400">
            — {attribution}
          </div>
        </div>
        <div className="col-span-12 md:col-span-4">
          <div className="border border-white/15 p-8 bg-black">
            <div className="overline mb-3">// {ctaHeading}</div>
            <p className="text-neutral-300 text-sm leading-relaxed mb-6">{ctaBody}</p>
            <Link
              to="/training"
              data-testid={`${id}-cta`}
              className="inline-flex items-center gap-2 bg-[#FFD300] text-black font-mono text-xs font-bold tracking-[0.18em] uppercase px-5 py-3 hover:bg-[#FFE149]"
            >
              Browse Courses <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Instructor() {
  return (
    <div data-testid="instructor-page">
      <Seo
        title="Our Instructors — S. Williams & Derek Kinard · Active Law Enforcement"
        description="Meet the instructor team at Secrecy Executive Training Academy — S. Williams (founder, D.C. law-enforcement veteran + multi-state firearms instructor) and Derek Kinard (active Maryland police officer, 2023 Anne Arundel County Police Academy graduate). Two active-duty officers, one curriculum."
        path="/instructor"
      />

      {/* ============ S. WILLIAMS ============ */}
      <InstructorHero
        id="williams"
        fileNo="003"
        role="Founder · Lead Instructor"
        photoSrc="/img/team/s-williams.jpg"
        photoAlt="S. Williams — Founder & Lead Instructor, Secrecy Executive Training Academy"
        name="S. Williams"
        intro="Founder of Secrecy Executive Training Academy, sworn law-enforcement veteran, and multi-state certified firearms instructor. Every course at SEP is designed by S. Williams and delivered by an active-duty instructor team held to the same law-enforcement standard."
        stats={[
          { n: "4",  l: "Years LE" },
          { n: "11", l: "Instructor Certs" },
          { n: "7",  l: "State Curricula" },
          { n: "16", l: "Courses Taught" },
        ]}
      />
      <CareerTimeline
        id="williams"
        sectionNo="001"
        heading="From the beat"
        headingHighlight="to the classroom."
        entries={WILLIAMS_CAREER}
      />
      <CredentialsGrid
        id="williams"
        sectionNo="002"
        headingA="Eleven active certifications."
        headingB="Zero shortcuts."
        subline="State, federal, and industry-recognized certifications — actively maintained through recertification requirements. Nothing on this list is legacy or expired."
        credentials={WILLIAMS_CREDENTIALS}
      />
      <InstructorQuote
        id="williams"
        attribution="S. Williams · Founder & Lead Instructor"
        ctaHeading="TRAIN WITH ME"
        ctaBody="Every class at SEP is taught by an active law-enforcement officer — S. Williams or Derek Kinard, never junior staff. Small cohorts. Direct feedback."
      >
        &ldquo;I teach the exact standard I was held to as a sworn officer. Legal, precise,
        and calm under pressure — that&rsquo;s not a slogan, that&rsquo;s what keeps you and your
        family <span className="text-[#FFD300]">on the right side of the moment.&rdquo;</span>
      </InstructorQuote>

      {/* ============ DEREK KINARD ============ */}
      <InstructorHero
        id="kinard"
        fileNo="004"
        role="Instructor · Active MD Police Officer"
        photoSrc="/img/team/derek-kinard.jpg"
        photoAlt="Derek Kinard — Instructor, Secrecy Executive Training Academy"
        name="Derek Kinard"
        intro="Instructor at Secrecy Executive Training Academy and active Maryland police officer. A 2023 graduate of the Anne Arundel County Police Academy, Derek brings current law-enforcement training, field experience, and practical knowledge into the classroom."
        stats={[
          { n: "2023", l: "Academy Grad" },
          { n: "5",    l: "Prof. Trainings" },
          { n: "MD",   l: "Active LE" },
          { n: "24/7", l: "Real Cases" },
        ]}
      />
      <CareerTimeline
        id="kinard"
        sectionNo="004"
        heading="From the street"
        headingHighlight="to the range."
        entries={KINARD_CAREER}
      />
      <CredentialsGrid
        id="kinard"
        sectionNo="005"
        headingA="Active law enforcement."
        headingB="Real-world application."
        subline="Professional training and field experience from an officer actively working the profession. Every credential below is current — no legacy certifications, no armchair theory."
        credentials={KINARD_CREDENTIALS}
      />
      <InstructorQuote
        id="kinard"
        attribution="Derek Kinard · Instructor · Active MD Police Officer"
        ctaHeading="TRAIN WITH DEREK"
        ctaBody="Learn from an officer who works the profession right now. Practical experience. Current law. Straight-talk feedback on the range."
      >
        &ldquo;Sound decision-making, officer safety, communication, situational awareness — these
        aren&rsquo;t buzzwords. They&rsquo;re what keeps my brothers and sisters on the job every day,
        and they&rsquo;re what I bring straight from the street into <span className="text-[#FFD300]">every class I teach.&rdquo;</span>
      </InstructorQuote>
    </div>
  );
}
